import { useContext, useCallback } from 'react';
import _ from 'lodash';
import postal from 'postal';
import ApplicationContext from '../ApplicationContext';
import JobListViewContext from '../components/common/JobListViewContext';
import useGlobalOkDialog from './useGlobalOkDialog';
import { RequestType } from '../fms.core/TrackRequest';
import { checkShippingLineSupport, isViewMode, getTrackRequestTitle } from '../util/trackingRequestUtils';

interface UseListTrackingRequestOptions {
  jobName: string;
  getMblNo: () => string;
  getContainerNo: () => string;
  getTrType: () => string;
  getTrStatus: () => string;
  getJobId: () => number;
  emptyMessage?: string;
}

export default function useListTrackingRequest(options: UseListTrackingRequestOptions) {
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const {
    jobName,
    getMblNo,
    getContainerNo,
    getTrType,
    getTrStatus,
    getJobId,
    emptyMessage = 'Master B/L number is empty or Master B/L has no container.',
  } = options;

  const trackRequestFunc = useCallback(async (): Promise<void> => {
    const mblNo = getMblNo();
    const containerNo = getContainerNo();
    const trType = getTrType();
    const trStatus = getTrStatus();
    const jobId = getJobId();

    if (_.isEmpty(mblNo) || _.isEmpty(containerNo)) {
      await showGlobalOkDialog(<p>{emptyMessage}</p>, false, null, 'warning');
      return;
    }

    const channel = postal.channel('ajax');

    channel.publish('ajax.loadingstart', {});
    try {
      if (isViewMode(trType, trStatus)) {
        const groupData = await appContext.api.getTrackRequest(jobName, jobId, trType, false);
        if (!groupData || groupData.length === 0) {
          await showGlobalOkDialog(<p>Cannot load tracking data.</p>);
          return;
        }
        jobListContext.setTrackingRequestModalState({
          jobId,
          trStatus,
          trType,
          containerNo,
          mode: 'view',
          vcResult: '',
          prefetchedGroupData: groupData,
        });
        return;
      }

      const apiResult = await checkShippingLineSupport(
        appContext.api,
        mblNo?.substring(0, 4),
        RequestType.bl,
        showGlobalOkDialog
      );

      if (!apiResult.success) return;

      const newTrType = apiResult.result === RequestType.bl ? 'M' : 'C';

      const groupData = await appContext.api.getTrackRequest(jobName, jobId, newTrType, true);
      if (!groupData || groupData.length === 0) {
        await showGlobalOkDialog(<p>Cannot load tracking data.</p>);
        return;
      }
      jobListContext.setTrackingRequestModalState({
        jobId,
        trStatus,
        trType: newTrType,
        containerNo,
        mode: 'new',
        vcResult: apiResult.result,
        prefetchedGroupData: groupData,
      });
    } finally {
      channel.publish('ajax.loadingend', {});
    }
  }, [
    appContext.api,
    jobListContext,
    jobName,
    getMblNo,
    getContainerNo,
    getTrType,
    getTrStatus,
    getJobId,
    emptyMessage,
    showGlobalOkDialog,
  ]);

  return { trackRequestFunc };
}

export { getTrackRequestTitle };
